import React, { useState, useMemo, useEffect } from "react";
import { X, Check, CircleNotch, CalendarX, ArrowsClockwise, Warning } from "@phosphor-icons/react";
import axios from "../../utils/axiosInstance";

const API = process.env.REACT_APP_BACKEND_URL;

const DAYS_PL = ["nd", "pn", "wt", "śr", "czw", "pt", "sob"];

function fmt(n) {
  return Number(n || 0).toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Modal that lets admin exclude specific days from a salary record and
 * see the recalculated Wynagrodzenie / Dopłata paliwowa / Przelew live.
 *
 * Preconditions: the record must have `daily_breakdown` populated (added on
 * PDF upload). If missing, we show a message asking to re-upload.
 */
export default function ExcludeDaysModal({ record, onClose, onSaved }) {
  const daily = useMemo(() => record?.daily_breakdown || {}, [record]);
  const days = useMemo(() => {
    // Sort by month-day
    return Object.keys(daily).sort((a, b) => {
      const [da, ma] = a.split(".").map(Number);
      const [db, mb] = b.split(".").map(Number);
      return ma !== mb ? ma - mb : da - db;
    });
  }, [daily]);

  const [excluded, setExcluded] = useState(new Set(record?.excluded_days || []));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  // Reset excluded set when record changes
  useEffect(() => {
    setExcluded(new Set(record?.excluded_days || []));
    setErr(null);
  }, [record?.id, record?.excluded_days]);

  const originalWyn = Number(record?.original_przychod_vat_brutto ?? record?.przychod_vat_brutto ?? 0);
  const originalDoplata = Number(record?.original_doplata_paliwowa ?? record?.doplata_paliwowa ?? 0);

  const dailyTotal = useMemo(() => {
    return days.reduce((s, d) => s + Number(daily[d] || 0), 0);
  }, [days, daily]);

  const excludedSum = useMemo(() => {
    let s = 0;
    for (const d of excluded) s += Number(daily[d] || 0);
    return s;
  }, [excluded, daily]);

  // Proportional model: every wynagrodzenie component (daily prowizja,
  // dopłata paliwowa, produkt dodatkowy, rabat) scales by the same ratio.
  // See backend /salary/{id}/exclude-days for the same formula.
  const remainingDaily = Math.max(0, dailyTotal - excludedSum);
  const ratio = dailyTotal > 0 ? remainingDaily / dailyTotal : 0;
  const newWyn = originalWyn * ratio;
  const newDoplata = originalDoplata * ratio;
  const newPrzychod = newWyn - newDoplata;

  // Factor to convert per-day prowizja → actual wynagrodzenie impact of that
  // day (day_amt × originalWyn / dailyTotal). Used for the column display so
  // what user sees per row = what will really be removed from wynagrodzenie.
  const dayImpactFactor = dailyTotal > 0 ? originalWyn / dailyTotal : 1;

  const toggle = (day) => {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(day)) next.delete(day);
      else next.add(day);
      return next;
    });
  };

  const selectAll = () => setExcluded(new Set(days));
  const clearAll = () => setExcluded(new Set());

  const parseDayToDate = (ddmm) => {
    // "16.07" + record.year → Date
    try {
      const [d, m] = ddmm.split(".").map((x) => parseInt(x, 10));
      const y = record?.year || new Date().getFullYear();
      return new Date(Date.UTC(y, m - 1, d));
    } catch {
      return null;
    }
  };

  const submit = async () => {
    setSaving(true);
    setErr(null);
    try {
      const { data } = await axios.post(
        `${API}/api/salary/${record.id}/exclude-days`,
        { excluded_days: [...excluded] }
      );
      onSaved && onSaved(data);
      onClose();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const hasBreakdown = days.length > 0;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="exclude-days-overlay"
    >
      <div
        className="bg-white rounded-xl border border-border max-w-2xl w-full flex flex-col max-h-[92vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="exclude-days-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4 shrink-0">
          <div>
            <h3 className="font-bold text-lg flex items-center gap-2">
              <CalendarX className="w-5 h-5 text-amber-600" weight="fill" />
              Wyklucz dni z wynagrodzenia
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {record?.employee_name} · {String(record?.period_start).padStart(2, "0")}–{String(record?.period_end).padStart(2, "0")}.{String(record?.month).padStart(2, "0")}.{record?.year}
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded" data-testid="exclude-days-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {!hasBreakdown ? (
          <div className="p-6 flex items-start gap-3 bg-amber-50 border-t border-amber-200">
            <Warning className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" weight="fill" />
            <div className="text-sm text-amber-900">
              <div className="font-semibold mb-1">Brak per-day rozbicia w tym rekordzie.</div>
              <p>
                Ta funkcja wymaga specyfikacji z tabelami dziennymi. Prześlij PDF specyfikacji jeszcze raz przez „Wgraj PDF” — po ponownym przesłaniu zobaczysz listę dni.
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Body — day list */}
            <div className="overflow-y-auto flex-1 p-5 space-y-3">
              <div className="flex items-center gap-2 text-xs">
                <span className="text-slate-500">Zaznacz dni, w których pracownik NIE pracował (jeździłeś zamiast niego):</span>
                <button onClick={selectAll} className="ml-auto text-slate-600 hover:text-slate-900 underline underline-offset-2">Wszystkie</button>
                <button onClick={clearAll} className="text-slate-600 hover:text-slate-900 underline underline-offset-2">Wyczyść</button>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {days.map((day) => {
                  const isExcluded = excluded.has(day);
                  const amt = Number(daily[day] || 0);
                  const impact = amt * dayImpactFactor; // total wynagrodzenie impact of this day
                  const dateObj = parseDayToDate(day);
                  const dow = dateObj ? DAYS_PL[dateObj.getUTCDay()] : "";
                  return (
                    <label
                      key={day}
                      className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-all ${
                        isExcluded
                          ? "bg-rose-50 border-rose-300 shadow-sm"
                          : "bg-white border-slate-200 hover:border-slate-400"
                      }`}
                      data-testid={`day-cb-${day}`}
                    >
                      <input
                        type="checkbox"
                        checked={isExcluded}
                        onChange={() => toggle(day)}
                        className="w-4 h-4 accent-rose-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm font-mono font-semibold ${isExcluded ? "line-through text-rose-700" : "text-slate-800"}`}>
                          {day} <span className="text-[10px] text-slate-500 font-sans font-normal">{dow}</span>
                        </div>
                        <div className={`text-xs font-mono tabular-nums ${isExcluded ? "text-rose-600" : "text-slate-800"}`}>
                          {fmt(impact)} zł
                        </div>
                        <div className={`text-[10px] font-mono tabular-nums ${isExcluded ? "text-rose-400" : "text-slate-400"}`}>
                          prowizja {fmt(amt)}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Footer — live recalculation preview */}
            <div className="border-t border-slate-200 bg-slate-50 p-5 shrink-0 space-y-2">
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                <div className="text-slate-500">Wykluczono dni:</div>
                <div className="text-right font-mono">
                  <span className="font-semibold">{excluded.size}</span>
                  <span className="text-slate-500 ml-1">/ {days.length}</span>
                </div>

                <div className="text-slate-500">Suma z wykluczonych dni:</div>
                <div className="text-right font-mono text-rose-600 tabular-nums">
                  −{fmt(excludedSum * dayImpactFactor)} zł
                  <div className="text-[10px] text-slate-400 font-normal">prowizja −{fmt(excludedSum)}</div>
                </div>

                <div className="text-slate-500 pt-2 border-t border-slate-200 col-span-2 my-1"></div>

                <div className="text-slate-500">Wynagrodzenie:</div>
                <div className="text-right font-mono tabular-nums">
                  <span className="text-slate-400 line-through">{fmt(originalWyn)}</span>
                  <span className="ml-2 font-bold text-slate-900">{fmt(newWyn)} zł</span>
                </div>

                <div className="text-slate-500">Dopłata paliwowa:</div>
                <div className="text-right font-mono tabular-nums text-xs">
                  <span className="text-slate-400 line-through">{fmt(originalDoplata)}</span>
                  <span className="ml-2 text-slate-700">{fmt(newDoplata)} zł</span>
                </div>

                <div className="text-slate-700 font-semibold pt-1 border-t border-slate-300">Przychód (netto):</div>
                <div className="text-right font-mono tabular-nums font-bold text-emerald-700 pt-1 border-t border-slate-300">
                  {fmt(newPrzychod)} zł
                </div>
              </div>

              {err && (
                <div className="text-xs text-rose-700 bg-rose-100 border border-rose-200 rounded p-2 mt-2">
                  {err}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={onClose}
                  className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-200 rounded"
                  data-testid="exclude-days-cancel"
                >
                  Anuluj
                </button>
                <button
                  onClick={submit}
                  disabled={saving}
                  className="px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white rounded text-sm font-semibold flex items-center gap-1.5"
                  data-testid="exclude-days-save"
                >
                  {saving ? <CircleNotch className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" weight="bold" />}
                  Zapisz
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
